import {
  listPublicProperties,
  getPublicProperty,
  type ListPublicPropertiesParams,
} from '@/lib/supabase/publicProperties';
import type {
  PublicPropertyDetail,
  PublicPropertyFilterInput,
  PublicPropertyFilterQueryParams,
  PublicPropertyFilterState,
  PublicPropertyListResult,
  SanitizedFilterResult,
} from '@/lib/types/publicProperty';
import {
  sanitizePublicPropertyFilters,
  parsePublicPropertyFilterQuery,
  buildPublicPropertyFilterQuery,
} from '@/lib/validation/publicProperties';
import { normalizeError } from '@/lib/services/errors';

export interface PublicPropertyListArgs extends Omit<ListPublicPropertiesParams, 'filters'> {
  filters?: PublicPropertyFilterInput | null;
  vacancyOnly?: boolean;
  onFiltersNormalized?: (state: PublicPropertyFilterState) => void;
  onAnalyticsEvent?: (event: PublicPropertyAnalyticsEvent) => void;
}

export type PublicPropertyAnalyticsEvent = {
  name: 'public.properties.filter';
  payload: PublicPropertyFilterState;
};

export async function fetchPublicPropertyList({
  page,
  pageSize,
  filters,
  vacancyOnly = false,
  signal,
  onFiltersNormalized,
  onAnalyticsEvent,
}: PublicPropertyListArgs = {}): Promise<PublicPropertyListResult> {
  const safeFilters = sanitizePublicPropertyFilters(filters);
  onFiltersNormalized?.(safeFilters);
  onAnalyticsEvent?.({
    name: 'public.properties.filter',
    payload: safeFilters,
  });

  const result = await listPublicProperties({
    page,
    pageSize,
    signal,
    filters: safeFilters,
    vacancyOnly,
  });

  return result;
}

export async function fetchPublicPropertyDetail(
  identifier: string,
  signal?: AbortSignal,
): Promise<PublicPropertyDetail> {
  try {
    const detail = await getPublicProperty({ identifier, signal });
    return detail;
  } catch (error) {
    throw normalizeError(error);
  }
}

export function derivePublicPropertyFilters(
  params: URLSearchParams | PublicPropertyFilterQueryParams,
): SanitizedFilterResult {
  return parsePublicPropertyFilterQuery(params);
}

export function serializePublicPropertyFilters(state: PublicPropertyFilterState): URLSearchParams {
  return buildPublicPropertyFilterQuery(state);
}
